import React from 'react';
import { cn } from '../../../lib/utils';
import { getEffortBucket } from '../../../design-system/effortScale';

/**
 * DayStatsTooltip — structured breakdown of one day's planning load (PRD §4.13).
 *
 * Dark glass for readability against any calendar background. Loading state
 * kept distinct from loaded-empty per approved rules. Mobile variant pins
 * inline instead of floating.
 */
export function DayStatsTooltip({
  date,
  stats,
  loading = false,
  variant = 'floating', // 'floating' | 'inline'
  className,
  ...rest
}) {
  const bucket = getEffortBucket(stats?.total_effort || 0);
  const outer = cn(
    'rounded-xl border text-xs font-medium shadow-lg',
    variant === 'floating'
      ? 'bg-slate-950 text-slate-50 border-white/10 p-3 w-[250px]'
      : 'bg-slate-950 text-slate-50 border-white/10 p-3 w-full',
    className,
  );
  return (
    <div className={outer} data-testid="date-stats-tooltip" role="tooltip" {...rest}>
      <div className="text-[11px] uppercase tracking-wider font-bold text-slate-400 mb-2">
        {date}
      </div>
      {loading ? (
        <Row label="Stats" value="Loading…" />
      ) : (
        <>
          <Row label="Tasks" value={stats?.task_count || 0} />
          <Row label="Meetings" value={stats?.meeting_count || 0} />
          <Row
            label="Total effort"
            value={
              <span style={{ color: bucket.color }} className="font-bold">
                {stats?.total_effort || 0} · {bucket.label}
              </span>
            }
          />
          <Row label="Personal" value={stats?.personal_effort || 0} />
          <Row label="Work" value={stats?.non_personal_effort || 0} />
        </>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1 border-t border-white/10 first:border-t-0 first:pt-0">
      <span className="text-slate-300">{label}</span>
      <span className="font-semibold tabular-nums">{value}</span>
    </div>
  );
}

export default DayStatsTooltip;
